/**
 * CARTO basemap tile URLs — the ONE owner (CR-033).
 *
 * Background: on ~2026-08-25 CARTO began requiring a (free) API key for its
 * public raster basemaps at basemaps.cartocdn.com. Unkeyed tiles still
 * return 200 but carry a diagonal "API KEY REQUIRED" watermark baked into
 * the PNG. The key is appended as `?key=` and is read from `VITE_CARTO_KEY`
 * at BUILD time (Vite bakes `import.meta.env` into the bundle — same
 * mechanism as `VITE_API_KEY`; the Dockerfile must ARG/ENV it or Railway
 * can't inject it).
 *
 * Fail-safe by design: a missing or blank key yields the plain URL, so a
 * misconfigured deploy degrades to the watermark — never a blank map and
 * never a failed build (`scripts/check-env-for-prod-build.mjs` only WARNS).
 *
 * The key is public-by-design (it ships in the JS bundle, like any browser
 * basemap key); protection is CARTO-side domain restriction, not secrecy.
 * The key may be (and in production is meant to be) domain-restricted
 * (CR-040) — which only works
 * because every CARTO TileLayer passes `CARTO_TILE_REFERRER_POLICY` (below)
 * so tiles send our origin. Any new host (GCP, GU servers) must be added to
 * the key's allowed domains BEFORE cutover or the basemap goes blank (403).
 * Never commit the value. Attribution to CARTO + OSM must remain visible
 * (CARTO's terms) — `LeafletMap` renders it; the insets share that map view.
 *
 * Raster CARTO basemaps are slated for retirement in favour of vector
 * basemaps (no date published, 2026-08). When that lands, this file is the
 * seam: swap the two constants (or the provider) and nothing else moves.
 */

import type { ReferrerPolicy as LeafletReferrerPolicy } from 'leaflet';

const VOYAGER_BASE = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
const LIGHT_BASE = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

/**
 * Append `key=<encoded>` to a tile URL template (`?` or `&` chosen by whether
 * the base already carries a query string). Blank/undefined → the base URL
 * unchanged. Encoding guarantees the value can't break the query string or
 * collide with Leaflet's `{placeholder}` templating.
 */
export function withCartoKey(baseUrl: string, key: string | undefined): string {
  const trimmed = key?.trim() ?? '';
  if (!trimmed) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}key=${encodeURIComponent(trimmed)}`;
}

const CARTO_KEY = (import.meta.env.VITE_CARTO_KEY as string | undefined) ?? '';

/** Main map basemap — CARTO Voyager (same style as the static prototype). */
export const VOYAGER_TILE_URL = withCartoKey(VOYAGER_BASE, CARTO_KEY);

/** AK / HI inset basemap — CARTO Positron ("light_all"). */
export const LIGHT_TILE_URL = withCartoKey(LIGHT_BASE, CARTO_KEY);

/**
 * CARTO serves every style from these four subdomains. Typed as a mutable
 * `string[]` so consumers can pass the SAME module-level instance to
 * react-leaflet's `subdomains` prop (no per-render spread/allocation).
 */
export const CARTO_TILE_SUBDOMAINS: string[] = ['a', 'b', 'c', 'd'];

/**
 * Per-tile `referrerPolicy` for every CARTO TileLayer (CR-040).
 *
 * The CARTO key is domain-restricted, so tile requests must carry our origin
 * as `Referer`. The site-wide policy is `no-referrer` (CR-032,
 * `serve.json.template`) and deliberately STAYS — this overrides it for the
 * tile <img> elements only (Leaflet sets `HTMLImageElement.referrerPolicy`
 * per tile), so nothing else on the page starts sending a Referer.
 *
 * `strict-origin`: origin only — never the path or query string (no leak of
 * which page/filters the visitor is on) — and nothing at all on an
 * HTTPS→HTTP downgrade. Dropping this from a layer = blank basemap in
 * production only (local dev servers don't send `no-referrer`); pinned by
 * unit tests and `e2e/map-tile-referrer.spec.ts`.
 */
export const CARTO_TILE_REFERRER_POLICY: LeafletReferrerPolicy = 'strict-origin';
